import { computed, signal } from '@preact/signals';
import { notificationsApi } from '@/lib/api/endpoints';
import { invalidate } from './queryCache';

export type NotificationSeverity = 'info' | 'warning' | 'urgent';

export interface NotificationItem {
  id: string;
  kind?: string;
  severity?: NotificationSeverity;
  title: string;
  body?: string;
  at: string;
  read: boolean;
  href?: string;
  actor?: string;
}

/**
 * Notifications store.
 *
 * Polling now handled via `useQuery` with `refetchInterval` in the
 * `useNotifications` hook (called from `NotificationCenter`). The store
 * keeps its own signals for optimistic mutation updates; the hook syncs
 * fetched data into `notifications` so `unreadCount` (computed) stays
 * reactive everywhere.
 */
export const notifications = signal<NotificationItem[]>([]);
export const unreadCount = computed(() => notifications.value.filter((n) => !n.read).length);
/** True si hay al menos una notificación urgente sin leer — resalta el badge. */
export const hasUrgentUnread = computed(() =>
  notifications.value.some((n) => !n.read && n.severity === 'urgent'),
);

/** Cache key used by useQuery — exported so mutations can invalidate it. */
export const NOTIFICATIONS_QUERY_KEY = 'notifications';

/** Default polling interval (ms). */
export const NOTIFICATIONS_POLL_INTERVAL = 60_000;

/** Fetcher for useQuery — returns the items array. */
export async function fetchNotifications(): Promise<NotificationItem[]> {
  const { data, error } = await notificationsApi.list();
  if (error || !data) return [];
  const items = (data as { items?: NotificationItem[] }).items;
  return Array.isArray(items) ? items : [];
}

export async function markAllRead(): Promise<void> {
  // Optimistic UI update; persist via backend; rollback on failure.
  notifications.value = notifications.value.map((n) => ({ ...n, read: true }));
  const { error } = await notificationsApi.markAllRead();
  if (error) {
    invalidate(NOTIFICATIONS_QUERY_KEY);
    return;
  }
  invalidate(NOTIFICATIONS_QUERY_KEY);
}

export async function markRead(id: string): Promise<void> {
  notifications.value = notifications.value.map((n) => (n.id === id ? { ...n, read: true } : n));
  const { error } = await notificationsApi.markRead(id);
  if (error) {
    invalidate(NOTIFICATIONS_QUERY_KEY);
    return;
  }
  invalidate(NOTIFICATIONS_QUERY_KEY);
}
